import { Box, TextField } from "@mui/material";
import { palette } from "../../themes/theme";
import * as turf from "@turf/turf";
import { updateTradeArea } from "../../redux/slices/mapSlice";
import PrimarySelect from "../PrimarySelect";
import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchIsochrone } from "../../api";
import mapboxgl from "mapbox-gl";

export const RadiusRingSelector = memo(
  ({
    index,
    addRingRadiusLayer,
    setSelectedValue,
    selectedValue,
    removeDriveTime,
    openColorSetting,
  }) => {
    const activeMarker = useSelector((state) =>
      state.map.tradeAreas.find((d) => d.id === state.map.activeMarker)
    );
    const dispatch = useDispatch();

    const [selectedType, setSelectedType] = useState(
      selectedValue[index]?.type
    );

    const handleRingRadiusChange = async (radius, type) => {
      const lngLat = [activeMarker.latlng.lng, activeMarker.latlng.lat];
      const ringName = `circleData-${index}-${lngLat[0]}-${lngLat[1]}`;
      if (type === "radius-ring") {
        const points = turf.featureCollection([]);
        points.features.push(turf.point(lngLat));
        const circle = turf.circle(turf.point(lngLat), parseFloat(radius), {
          units: "miles",
        });
        addRingRadiusLayer({
          name: ringName,
          coordinates: circle,
          setting: activeMarker.radiusSetting[index],
          radius,
          type: "radius",
          index,
        });
        return;
      }
      if (type === "drive-time") {
        if (radius <= 0) {
          removeDriveTime(ringName);
          return;
        }
        const isochrone = await fetchIsochrone({
          lat: activeMarker.latlng.lat,
          lng: activeMarker.latlng.lng,
          minutes: radius,
          accessToken: mapboxgl.accessToken,
        });
        dispatch(
          updateTradeArea({
            id: activeMarker.id,
            value: {
              radiusSetting: {
                order: index,
                radiusCoordinates: isochrone,
              },
            },
          })
        );
        addRingRadiusLayer({
          name: ringName,
          coordinates: isochrone,
          setting: activeMarker.radiusSetting[index],
          radius,
          type: "drive-time",
          index,
        });
        return;
      }
    };

    const handleTypeChange = (e) => {
      const type = e.target.value;
      dispatch(
        updateTradeArea({
          id: activeMarker.id,
          value: {
            radiusSetting: {
              order: String(index),
              type,
            },
          },
        })
      );
      if (type) {
        setSelectedValue((prev) => ({
          ...prev,
          [index]: {
            type,
            size: Number(activeMarker.radiusSetting[index].defaultRadius),
          },
        }));
        setSelectedType(type);
        handleRingRadiusChange(
          Number(activeMarker.radiusSetting[index].defaultRadius),
          type
        );
        return;
      }

      setSelectedValue((prev) => ({
        ...prev,
        [index]: {
          type,
          size: 0,
        },
      }));
      handleRingRadiusChange(0, selectedValue[index].type);
    };

    const handleSizeChange = (e) => {
      const radius = e.target.value > 0 ? e.target.value : 0;
      setSelectedValue((prev) => ({
        ...prev,
        [index]: { ...prev[index], size: radius },
      }));
      dispatch(
        updateTradeArea({
          id: activeMarker.id,
          value: {
            radiusSetting: {
              order: String(index),
              defaultRadius: radius,
            },
          },
        })
      );
      handleRingRadiusChange(radius, selectedValue[index].type);
    };

    useEffect(() => {
      setSelectedType(selectedValue[index]?.type);
    }, [selectedValue]);

    return (
      <Box sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
        <PrimarySelect
          values={[
            { title: "Radius Ring", value: "radius-ring" },
            // { title: "Drive Time (minutes)", value: "drive-time" },
          ]}
          selectedValue={selectedType || ""}
          onChange={handleTypeChange}
        />
        <TextField
          disabled={Boolean(!selectedValue[index]?.type)}
          type="number"
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              color: "black",
              borderRadius: "4px",
              "& fieldset": {
                borderColor: "black", // default border
              },
              "&:hover fieldset": {
                borderColor: "black", // on hover
              },
              "&.Mui-focused fieldset": {
                borderColor: "black", // on focus
              },
            },
            "& .MuiInputLabel-root": {
              color: "black", // label color
            },
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
              {
                display: "none",
              },
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
            width: "80px",
            alignSelf: "end",
          }}
          label={selectedType === "drive-time" ? "Min" : "Size"}
          inputMode="decimal"
          value={
            selectedValue[index]?.size ||
            activeMarker.radiusSetting[index].defaultRadius
          }
          onChange={handleSizeChange}
          // slotProps={{ htmlInput: { component } }}
          onBlur={() => {
            console.log("blurred");
          }}
        />
        {/* TODO: uncomment below code once color setting work is done. */}
        {selectedValue[index]?.type && (
          <div
            style={{
              width: "52px",
              height: "32px",
              alignSelf: "end",
              borderRadius: "6px",
              border: `1px solid ${palette.primary.main}`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => openColorSetting(index)}
          >
            <Box
              sx={{
                width: "62%",
                height: "70%",
                background:
                  activeMarker.radiusSetting[index].defaultRingRadiusColor,
                borderRadius: "25px",
              }}
            />
          </div>
        )}
      </Box>
    );
  }
);
